import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

export enum TakeoverId {
  UTILITY_CONNECT = 'utility-connect',
  BADGE_DETAIL = 'badge-detail',
  BADGE_EARNED = 'badge-earned',
  SOLAR_CONNECT = 'solar-connect',
  REWARDS = 'rewards',
  ONBOARDING_COMPLETION = 'onboarding-completion',
  FINALIZING_VERIFICATION = 'finalizing-verification',
  ONE_TIME_PAYMENT = 'one-time-payment',
}

export interface TakeoverData {
  id: TakeoverId;
  props?: Record<string, any>;
}

export interface TakeoverState {
  isOpen: boolean;
  current: TakeoverData | null;
  queue: TakeoverData[];
}

export const useTakeover = () => {
  const [takeoverState, setTakeoverState] = useState<TakeoverState>({
    isOpen: false,
    current: null,
    queue: [],
  });

  const search = useSearch({ strict: false });
  const navigate = useNavigate();

  useEffect(() => {
    const TakeoverId = search.takeover;

    if (TakeoverId) {
      let props = {};
      if (search.takeoverProps) {
        try {
          props = search.takeoverProps;
        } catch (error) {
          console.warn('Failed to parse takeover props:', error);
        }
      }

      setTakeoverState({
        isOpen: true,
        current: { id: TakeoverId, props },
        queue: [],
      });
      navigate({
        search: undefined,
        replace: true,
      });
    }
  }, [search, navigate]);

  const openTakeover = (id: TakeoverId, props?: Record<string, any>) => {
    setTakeoverState((prev) => {
      const newState = {
        isOpen: true,
        current: { id, props },
        queue: prev.current ? [...prev.queue, prev.current] : prev.queue,
      };
      return newState;
    });
  };

  const closeTakeover = () => {
    setTakeoverState((prev) => {
      // If there are queued takeovers, open the most recent one
      if (prev.queue.length > 0) {
        const previousTakeover = prev.queue[prev.queue.length - 1];
        const remainingQueue = prev.queue.slice(0, -1);
        const newState = {
          isOpen: true,
          current: previousTakeover,
          queue: remainingQueue,
        };
        return newState;
      }

      // Otherwise, close completely
      const newState = {
        isOpen: false,
        current: null,
        queue: [],
      };
      return newState;
    });
  };

  const closeAllTakeovers = () => {
    setTakeoverState({
      isOpen: false,
      current: null,
      queue: [],
    });
  };

  return {
    takeoverState,
    openTakeover,
    closeTakeover,
    closeAllTakeovers,
  };
};
